import React, { useState, useEffect } from "react";
import { authService, companyService } from "../../services/api";

export default function LoginPage({ onLogin }) {
  const [userType, setUserType] = useState("admin");
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [logoColors, setLogoColors] = useState({
    primary: "#334155", // default slate-700
    secondary: "#1e293b", // default slate-800
    light: "#64748b", // default slate-500
  });

  // Extract dominant color from image
  const extractColorsFromLogo = (imageUrl) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Sample colors from the image
        const colorMap = {};
        for (let i = 0; i < data.length; i += 4 * 10) {
          // Sample every 10th pixel for performance
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          // Skip transparent and very light/white pixels
          if (a < 128 || (r > 240 && g > 240 && b > 240)) continue;

          // Round to nearest 32 to group similar colors
          const key = `${Math.round(r / 32) * 32},${Math.round(g / 32) * 32},${
            Math.round(b / 32) * 32
          }`;
          colorMap[key] = (colorMap[key] || 0) + 1;
        }

        // Find the most common color
        let maxCount = 0;
        let dominantColor = null;
        for (const [color, count] of Object.entries(colorMap)) {
          if (count > maxCount) {
            maxCount = count;
            dominantColor = color;
          }
        }

        if (dominantColor) {
          const [r, g, b] = dominantColor.split(",").map(Number);

          // Generate color variations
          const primary = `rgb(${r}, ${g}, ${b})`;
          const secondary = `rgb(${Math.max(0, r - 30)}, ${Math.max(
            0,
            g - 30
          )}, ${Math.max(0, b - 30)})`;
          const light = `rgb(${Math.min(255, r + 50)}, ${Math.min(
            255,
            g + 50
          )}, ${Math.min(255, b + 50)})`;

          setLogoColors({ primary, secondary, light });
        }
      } catch (error) {
        console.error("Error extracting colors from logo:", error);
      }
    };
    img.src = imageUrl;
  };

  // Fetch company info based on current domain
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        // First try to get from localStorage like Home.jsx
        const storedCompanyData = localStorage.getItem("companyData");
        if (storedCompanyData) {
          const parsedData = JSON.parse(storedCompanyData);
          setCompanyInfo(parsedData);

          // Extract colors from logo if available
          if (parsedData?.logo) {
            extractColorsFromLogo(parsedData.logo);
          }
          setCompanyLoading(false);
          return;
        }

        // If not in localStorage, fetch from API
        const data = await companyService.getCompanyByDomain();

        if (data.success && data.data && data.data.company) {
          setCompanyInfo(data.data.company);

          // Extract colors from logo if available
          if (data.data.company?.logo) {
            extractColorsFromLogo(data.data.company.logo);
          }
        } else {
          console.warn("Company info not found or invalid response");
        }
      } catch (error) {
        console.error("Failed to fetch company info:", error);
        // Don't show error to user, just log it
      } finally {
        setCompanyLoading(false);
      }
    };

    fetchCompanyInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await authService.login(
        credentials.email,
        credentials.password,
        userType
      );
      if (response.success) {
        // Determine backend user type
        const backendUserType =
          response.user.role == "admin" || response.user.role == "super_admin"
            ? "admin"
            : "employee";

        // Validate frontend user type selection against backend role
        if (userType !== backendUserType) {
          const roleMismatchMessage =
            userType === "admin"
              ? 'This email is registered as an employee. Please select "Employee" user type to login.'
              : 'This email is registered as an admin. Please select "Admin" user type to login.';
          setError(roleMismatchMessage);
          setLoading(false);
          return;
        }

        // Role validation passed - proceed with login
        localStorage.setItem("isAuthenticated", "true");
        setUserType(backendUserType);
        localStorage.setItem("userType", backendUserType);

        // Store user ID for employee profile access
        if (response?.employee?.id) {
          localStorage.setItem("userId", response.employee.id);
        } else if (response?.user?.id) {
          localStorage.setItem("userId", response.user.id);
        }

        // Store company data if available in the response
        if (response.company) {
          localStorage.setItem("companyData", JSON.stringify(response.company));
        }

        onLogin(backendUserType);
      } else {
        setError(response.message || "Login failed");
      }
    } catch (error) {
      setError(error.message || "An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordMessage("");
    setForgotPasswordLoading(true);

    try {
      const response = await authService.forgotPassword(
        forgotPasswordEmail,
        userType
      );
      if (response.success) {
        setForgotPasswordMessage(response.message);
        setForgotPasswordEmail("");
        // Close modal after 3 seconds
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotPasswordMessage("");
        }, 3000);
      } else {
        setForgotPasswordMessage(
          response.message || "Failed to send reset email"
        );
      }
    } catch (error) {
      setForgotPasswordMessage(error.message || "An error occurred");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white dark:bg-gray-900">
      {/* Left Side - HRMS Gears Showcase */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-12 flex-col justify-center items-center relative overflow-hidden">
        {/* Content */}
        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Welcome to PaceHRM
            </h1>
            <p className="text-md text-gray-600 dark:text-gray-400">
              Complete Human Resource Management Solution
            </p>
          </div>

          {/* Gear System - Employee Lifecycle Flow */}
          <div className="space-y-8">
            {/* Gear 1 - Onboarding */}
            <div className="flex items-center gap-4">
              <div className="relative group flex-shrink-0">
                <svg
                  className="w-16 h-16 relative"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="none"
                    stroke="url(#green-grad)"
                    strokeWidth="3"
                  />
                  <circle cx="50" cy="15" r="5" fill="url(#green-grad)" />
                  <circle cx="73.5" cy="26.5" r="5" fill="url(#green-grad)" />
                  <circle cx="84.5" cy="50" r="5" fill="url(#green-grad)" />
                  <circle cx="73.5" cy="73.5" r="5" fill="url(#green-grad)" />
                  <circle cx="50" cy="85" r="5" fill="url(#green-grad)" />
                  <circle cx="26.5" cy="73.5" r="5" fill="url(#green-grad)" />
                  <circle cx="15.5" cy="50" r="5" fill="url(#green-grad)" />
                  <circle cx="26.5" cy="26.5" r="5" fill="url(#green-grad)" />
                  <circle
                    cx="50"
                    cy="50"
                    r="15"
                    fill="white"
                    stroke="url(#green-grad)"
                    strokeWidth="2"
                  />
                  <defs>
                    <linearGradient
                      id="green-grad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#4ade80" />
                      <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                  Onboarding
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Start the employee journey
                </p>
              </div>
            </div>

            {/* Flow Arrow */}
            <div className="flex justify-center">
              <svg
                className="w-8 h-8 text-gray-400 dark:text-gray-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Gear 2 - Employee Management */}
            <div className="flex items-center gap-4">
              <div className="relative group flex-shrink-0">
                <svg
                  className="w-16 h-16 relative"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="none"
                    stroke="url(#blue-grad)"
                    strokeWidth="3"
                  />
                  <circle cx="50" cy="15" r="5" fill="url(#blue-grad)" />
                  <circle cx="73.5" cy="26.5" r="5" fill="url(#blue-grad)" />
                  <circle cx="84.5" cy="50" r="5" fill="url(#blue-grad)" />
                  <circle cx="73.5" cy="73.5" r="5" fill="url(#blue-grad)" />
                  <circle cx="50" cy="85" r="5" fill="url(#blue-grad)" />
                  <circle cx="26.5" cy="73.5" r="5" fill="url(#blue-grad)" />
                  <circle cx="15.5" cy="50" r="5" fill="url(#blue-grad)" />
                  <circle cx="26.5" cy="26.5" r="5" fill="url(#blue-grad)" />
                  <circle
                    cx="50"
                    cy="50"
                    r="15"
                    fill="white"
                    stroke="url(#blue-grad)"
                    strokeWidth="2"
                  />
                  <defs>
                    <linearGradient
                      id="blue-grad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#60a5fa" />
                      <stop offset="100%" stopColor="#4f46e5" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                  Management
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Control and monitor operations
                </p>
              </div>
            </div>

            {/* Flow Arrow */}
            <div className="flex justify-center">
              <svg
                className="w-8 h-8 text-gray-400 dark:text-gray-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Gear 3 - Attendance */}
            <div className="flex items-center gap-4">
              <div className="relative group flex-shrink-0">
                <svg
                  className="w-16 h-16 relative"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="none"
                    stroke="url(#purple-grad)"
                    strokeWidth="3"
                  />
                  <circle cx="50" cy="15" r="5" fill="url(#purple-grad)" />
                  <circle cx="73.5" cy="26.5" r="5" fill="url(#purple-grad)" />
                  <circle cx="84.5" cy="50" r="5" fill="url(#purple-grad)" />
                  <circle cx="73.5" cy="73.5" r="5" fill="url(#purple-grad)" />
                  <circle cx="50" cy="85" r="5" fill="url(#purple-grad)" />
                  <circle cx="26.5" cy="73.5" r="5" fill="url(#purple-grad)" />
                  <circle cx="15.5" cy="50" r="5" fill="url(#purple-grad)" />
                  <circle cx="26.5" cy="26.5" r="5" fill="url(#purple-grad)" />
                  <circle
                    cx="50"
                    cy="50"
                    r="15"
                    fill="white"
                    stroke="url(#purple-grad)"
                    strokeWidth="2"
                  />
                  <defs>
                    <linearGradient
                      id="purple-grad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#c084fc" />
                      <stop offset="100%" stopColor="#ec4899" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                  Attendance
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Track and report presence
                </p>
              </div>
            </div>

            {/* Flow Arrow */}
            <div className="flex justify-center">
              <svg
                className="w-8 h-8 text-gray-400 dark:text-gray-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Gear 4 - Payroll */}
            <div className="flex items-center gap-4">
              <div className="relative group flex-shrink-0">
                <svg
                  className="w-16 h-16 relative"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="none"
                    stroke="url(#orange-grad)"
                    strokeWidth="3"
                  />
                  <circle cx="50" cy="15" r="5" fill="url(#orange-grad)" />
                  <circle cx="73.5" cy="26.5" r="5" fill="url(#orange-grad)" />
                  <circle cx="84.5" cy="50" r="5" fill="url(#orange-grad)" />
                  <circle cx="73.5" cy="73.5" r="5" fill="url(#orange-grad)" />
                  <circle cx="50" cy="85" r="5" fill="url(#orange-grad)" />
                  <circle cx="26.5" cy="73.5" r="5" fill="url(#orange-grad)" />
                  <circle cx="15.5" cy="50" r="5" fill="url(#orange-grad)" />
                  <circle cx="26.5" cy="26.5" r="5" fill="url(#orange-grad)" />
                  <circle
                    cx="50"
                    cy="50"
                    r="15"
                    fill="white"
                    stroke="url(#orange-grad)"
                    strokeWidth="2"
                  />
                  <defs>
                    <linearGradient
                      id="orange-grad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#fb923c" />
                      <stop offset="100%" stopColor="#ef4444" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                  Payroll
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Process and disburse salaries
                </p>
              </div>
            </div>

            {/* Flow Arrow */}
            <div className="flex justify-center">
              <svg
                className="w-8 h-8 text-gray-400 dark:text-gray-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Gear 5 - Offboarding */}
            <div className="flex items-center gap-4">
              <div className="relative group flex-shrink-0">
                <svg
                  className="w-16 h-16 relative"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="none"
                    stroke="url(#gray-grad)"
                    strokeWidth="3"
                  />
                  <circle cx="50" cy="15" r="5" fill="url(#gray-grad)" />
                  <circle cx="73.5" cy="26.5" r="5" fill="url(#gray-grad)" />
                  <circle cx="84.5" cy="50" r="5" fill="url(#gray-grad)" />
                  <circle cx="73.5" cy="73.5" r="5" fill="url(#gray-grad)" />
                  <circle cx="50" cy="85" r="5" fill="url(#gray-grad)" />
                  <circle cx="26.5" cy="73.5" r="5" fill="url(#gray-grad)" />
                  <circle cx="15.5" cy="50" r="5" fill="url(#gray-grad)" />
                  <circle cx="26.5" cy="26.5" r="5" fill="url(#gray-grad)" />
                  <circle
                    cx="50"
                    cy="50"
                    r="15"
                    fill="white"
                    stroke="url(#gray-grad)"
                    strokeWidth="2"
                  />
                  <defs>
                    <linearGradient
                      id="gray-grad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#9ca3af" />
                      <stop offset="100%" stopColor="#475569" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                  Offboarding
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Complete exit process
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-gray-50 to-slate-100 dark:from-gray-900 dark:to-gray-950">
        <div className="max-w-lg w-full bg-white dark:bg-gray-800 shadow-xl">
          {/* Corporate Header with Logo */}
          <div
            className="px-10 py-8"
            style={{
              background: `linear-gradient(to right, ${logoColors.primary}, ${logoColors.secondary})`,
            }}
          >
            <div className="text-center">
              {/* Company Logo - Same as Home.jsx */}
              <div className="mb-4 flex justify-center">
                {companyInfo?.logo ? (
                  <img
                    src={companyInfo.logo}
                    alt={`${companyInfo.name} logo`}
                    className="h-20 w-auto object-contain"
                    onError={(e) => {
                      // If logo fails to load, hide the img and show SVG
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />
                ) : null}
                <svg
                  className={`w-20 h-20 text-white ${
                    companyInfo?.logo ? "hidden" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>

              {/* Company Info Display */}
              {!companyLoading && companyInfo && (
                <div className="mb-2">
                  <h2 className="text-2xl font-bold text-white">
                    {companyInfo.name}
                  </h2>
                </div>
              )}

              {companyLoading && (
                <div className="mb-2">
                  <div className="animate-pulse">
                    <div className="h-6 bg-slate-600 rounded w-48 mx-auto"></div>
                  </div>
                </div>
              )}

              <h1 className="text-xl font-semibold text-slate-200">
                Sign In to Your Account
              </h1>
            </div>
          </div>

          {/* Form Content */}
          <div className="px-10 py-8">
            {error && (
              <div
                className="mb-6 bg-red-50 border-l-4 border-red-500 dark:bg-red-900/20 dark:border-red-600 px-4 py-3"
                role="alert"
              >
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-500 dark:text-red-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-red-700 dark:text-red-300">
                    {error}
                  </span>
                </div>
              </div>
            )}
            <form className="space-y-5" onSubmit={handleSubmit}>
              {/* User Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  User Type <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={userType}
                    onChange={(e) => {
                      setUserType(e.target.value);
                      setError(""); // Clear any previous role mismatch errors
                    }}
                    className="block w-full py-3 px-4 pr-10 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-medium focus:outline-none focus:ring-2 transition-all duration-200 appearance-none cursor-pointer"
                    style={{
                      "--tw-ring-color": logoColors.light,
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = logoColors.primary;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "";
                    }}
                  >
                    <option value="admin">Admin</option>
                    <option value="employee">Employee</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Select your account type to continue
                </p>
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                      />
                    </svg>
                  </div>
                  <input
                    type="email"
                    name="username"
                    autoComplete="username"
                    required
                    value={credentials.email}
                    onChange={(e) =>
                      setCredentials({ ...credentials, email: e.target.value })
                    }
                    className="block w-full py-3 pl-12 pr-4 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 transition-all duration-200"
                    placeholder="your.email@company.com"
                    style={{
                      "--tw-ring-color": logoColors.light,
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = logoColors.primary;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "";
                    }}
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    required
                    value={credentials.password}
                    onChange={(e) =>
                      setCredentials({
                        ...credentials,
                        password: e.target.value,
                      })
                    }
                    className="block w-full py-3 pl-12 pr-12 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 transition-all duration-200"
                    placeholder="Enter your password"
                    style={{
                      "--tw-ring-color": logoColors.light,
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = logoColors.primary;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors duration-200"
                  >
                    {showPassword ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 border-gray-300 rounded cursor-pointer"
                    style={{
                      accentColor: logoColors.primary,
                    }}
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    Remember me
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm font-medium transition-colors duration-200"
                  style={{ color: logoColors.primary }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = logoColors.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = logoColors.primary;
                  }}
                >
                  Forgot password?
                </button>
              </div>

              {/* Sign In Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center items-center py-3.5 px-4 border border-transparent font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 shadow-md hover:shadow-lg ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  style={{
                    background: `linear-gradient(to right, ${logoColors.primary}, ${logoColors.secondary})`,
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.background = `linear-gradient(to right, ${logoColors.secondary}, ${logoColors.primary})`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = `linear-gradient(to right, ${logoColors.primary}, ${logoColors.secondary})`;
                  }}
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <svg
                        className="ml-2 w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-900 px-10 py-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">
              By signing in, you agree to our Terms of Service and Privacy
              Policy
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div
              className="px-8 py-6"
              style={{
                background: `linear-gradient(to right, ${logoColors.primary}, ${logoColors.secondary})`,
              }}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <svg
                    className="w-6 h-6 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                    />
                  </svg>
                  Reset Password
                </h3>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail("");
                    setForgotPasswordMessage("");
                  }}
                  className="text-slate-300 hover:text-white transition-colors duration-200"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-8 py-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Enter your email address and we'll send you a new password to
                access your account.
              </p>

              {forgotPasswordMessage && (
                <div
                  className={`mb-5 p-3 border-l-4 ${
                    forgotPasswordMessage.includes("successfully")
                      ? "bg-green-50 border-green-500 dark:bg-green-900/20 dark:border-green-600"
                      : "bg-red-50 border-red-500 dark:bg-red-900/20 dark:border-red-600"
                  }`}
                >
                  <div className="flex items-center">
                    <svg
                      className={`w-5 h-5 mr-2 ${
                        forgotPasswordMessage.includes("successfully")
                          ? "text-green-500"
                          : "text-red-500"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      {forgotPasswordMessage.includes("successfully") ? (
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      ) : (
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      )}
                    </svg>
                    <p
                      className={`text-sm font-medium ${
                        forgotPasswordMessage.includes("successfully")
                          ? "text-green-700 dark:text-green-300"
                          : "text-red-700 dark:text-red-300"
                      }`}
                    >
                      {forgotPasswordMessage}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                        />
                      </svg>
                    </div>
                    <input
                      type="email"
                      name="forgot-password-email"
                      autoComplete="email"
                      required
                      placeholder="your.email@company.com"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className="w-full py-3 pl-12 pr-4 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 transition-all duration-200"
                      style={{
                        "--tw-ring-color": logoColors.light,
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = logoColors.primary;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "";
                      }}
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail("");
                      setForgotPasswordMessage("");
                    }}
                    className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className={`flex-1 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 font-semibold shadow-md ${
                      forgotPasswordLoading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    style={{
                      background: `linear-gradient(to right, ${logoColors.primary}, ${logoColors.secondary})`,
                    }}
                    onMouseEnter={(e) => {
                      if (!forgotPasswordLoading) {
                        e.currentTarget.style.background = `linear-gradient(to right, ${logoColors.secondary}, ${logoColors.primary})`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${logoColors.primary}, ${logoColors.secondary})`;
                    }}
                  >
                    {forgotPasswordLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      "Send Reset Email"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
